import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories, siteInfo } from "@/mocks/content";

const mainLinks = [
  { label: "关于我们", to: "/about" },
  { label: "联系我们", to: "/contact" },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [pathname]);

  const isHome = pathname === "/";
  const onHero = isHome && !scrolled && !mobileOpen;

  const idleLink = onHero
    ? "text-white/90 hover:text-white hover:bg-white/10"
    : "text-foreground-700 hover:text-foreground-950 hover:bg-background-200/70";
  const activeLink = onHero
    ? "text-white bg-white/15"
    : "text-primary-700 bg-primary-100/70";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        onHero ? "bg-transparent" : "bg-background-50/95 backdrop-blur border-b border-background-200"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center font-heading font-bold text-lg">
                料
              </span>
              <span
                className={`font-heading font-bold text-xl tracking-tight ${
                  onHero ? "text-white" : "text-foreground-950"
                }`}
              >
                {siteInfo.name}
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
              <Link
                to="/"
                className={`px-3 py-2 text-sm rounded-md cursor-pointer transition-colors ${
                  pathname === "/" ? activeLink : idleLink
                }`}
              >
                首页
              </Link>

              <div
                className="relative"
                onMouseEnter={() => setCatOpen(true)}
                onMouseLeave={() => setCatOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setCatOpen((v) => !v)}
                  className={`px-3 py-2 text-sm rounded-md cursor-pointer transition-colors flex items-center gap-1 whitespace-nowrap ${
                    pathname.startsWith("/category") ? activeLink : idleLink
                  }`}
                  aria-expanded={catOpen}
                >
                  热门分类
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-base" />
                  </span>
                </button>
                {catOpen && (
                  <div className="absolute left-0 top-full pt-2 w-64">
                    <div className="bg-background-50 border border-background-200 rounded-lg p-2">
                      {categories.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/category/${c.slug}`}
                          className="flex items-start gap-3 px-3 py-2 rounded-md cursor-pointer hover:bg-background-100 transition-colors"
                        >
                          <span className="w-5 h-5 mt-0.5 flex items-center justify-center text-secondary-600">
                            <i className={`${c.icon} text-base`} />
                          </span>
                          <span>
                            <span className="block text-sm text-foreground-900">{c.name}</span>
                            <span className="block text-xs text-foreground-500 mt-0.5">
                              {c.count} 条内容
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {mainLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`px-3 py-2 text-sm rounded-md cursor-pointer transition-colors ${
                    pathname === l.to ? activeLink : idleLink
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <span
              className={`text-xs flex items-center gap-1.5 ${
                onHero ? "text-white/80" : "text-foreground-500"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                <i className="ri-refresh-line text-base" />
              </span>
              持续更新中
            </span>
            <Link
              to="/category/entertainment"
              className="px-4 py-2 text-sm bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              开始翻看
            </Link>
          </div>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              onHero ? "text-white" : "text-foreground-900"
            }`}
          >
            <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            <Link
              to="/"
              className="px-3 py-3 text-sm text-foreground-800 rounded-md hover:bg-background-100 cursor-pointer"
            >
              首页
            </Link>
            {mainLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="px-3 py-3 text-sm text-foreground-800 rounded-md hover:bg-background-100 cursor-pointer"
              >
                {l.label}
              </Link>
            ))}
            <div className="px-3 pt-3 pb-1 text-xs text-foreground-500">热门分类</div>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="flex items-center gap-2 px-3 py-2.5 text-sm bg-background-100 rounded-md text-foreground-800 cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-secondary-600">
                    <i className={`${c.icon} text-base`} />
                  </span>
                  {c.name}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}